// utils/colorScales.js

/** Clamp a value between 0 and 1 */
export const clamp01 = (x) => Math.max(0, Math.min(1, x));

/** Convert a hex color to HSL */
export const hexToHsl = (hex) => {
  let c = hex.replace("#", "");
  if (c.length === 3) c = c.split("").map((x) => x + x).join("");
  const r = parseInt(c.slice(0, 2), 16) / 255;
  const g = parseInt(c.slice(2, 4), 16) / 255;
  const b = parseInt(c.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s;
  const l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      default:
        break;
    }
    h /= 6;
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
};

/** Convert HSL back to hex */
export const hslToHex = (h, s, l) => {
  h /= 360;
  s /= 100;
  l /= 100;
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
  const g = Math.round(hue2rgb(p, q, h) * 255);
  const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);
  return "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
};

/** Create a single-color light→dark heat scale */
export const makeHeatScale = (baseHex, values) => {
  const finite = values.filter((v) => Number.isFinite(v));
  const min = finite.length ? Math.min(...finite) : 0;
  const max = finite.length ? Math.max(...finite) : 100;
  const { h, s } = hexToHsl(baseHex);

  return (v) => {
    if (!Number.isFinite(v)) return "#CCCCCC";
    const t = clamp01((v - min) / Math.max(1e-6, max - min));
    const light = 95 - t * 60;
    return hslToHex(h, s, light);
  };
};

/** Create a two-sided (diverging) heat scale for positive and negative values */
export const make2pointHeatScale = (baseHexPos, baseHexNeg, values) => {
  const finite = values.filter((v) => Number.isFinite(v));
  const min = finite.length ? Math.min(...finite) : 0;
  const max = finite.length ? Math.max(...finite) : 100;

  return (v) => {
    if (!Number.isFinite(v)) return "#CCCCCC";
    if (v >= 0) {
      const { h, s } = hexToHsl(baseHexPos);
      const t = clamp01(v / Math.max(1e-6, max));
      const light = 95 - t * 60;
      return hslToHex(h, s, light);
    } else {
      const { h, s } = hexToHsl(baseHexNeg);
      const t = clamp01(v / Math.min(-1e-6, min));
      const light = 95 - t * 60;
      return hslToHex(h, s, light);
    }
  };
};

/** Determine color for a likert key based on polarity */
export function baseColorForLikertKey(group, key) {
  const pol = group?.polarity || {};
  const tag = Object.entries(pol).find(([_, polKey]) => polKey === key)?.[0];

  if (tag === "pos") return "#2E7D32";  // green for positive
  if (tag === "neg") return "#C62828";  // red for negative
  return "#6e6e6e";                      // neutral gray
}

/** Build labels and colors for any config set safely */
export const buildLabelsAndColors = (groupsById, theme_colours) => {
  const labels = {};
  const colors = {};

  Object.values(groupsById).forEach((g) => {
    let i = 0;
    g.keys.forEach((k) => {
      // Label preference: full_labels > labels > key
      labels[k] = g.full_labels?.[k] || g.labels?.[k] || k;

      // Color logic hierarchy:
      if (g.colors?.[k]) {
        colors[k] = g.colors[k];
      } else if (g.type === "likert" && g.polarity) {
        colors[k] = baseColorForLikertKey(g, k);
      } else {
        colors[k] = theme_colours[i % theme_colours.length];
        i += 1;
      }
    });
  });

  return { labels, colors };
};
